/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { MapPin, User, Clock, Coins, Phone, Truck } from 'lucide-react';
import { motion } from 'motion/react';
import { useIsMobile } from '../utils/useIsMobile';
import { cloudinaryThumb } from '../utils/imageUrl';

export default function ProductCard({ product, onClick, footerActions }) {
  const isMobile = useIsMobile();
  return (
    <motion.div
      whileHover={{ y: -8 }}
      className="organic-card group h-full flex flex-col"
    >
      <div
        onClick={onClick}
        className="relative aspect-[4/3] overflow-hidden flex-shrink-0 cursor-pointer"
      >
        <img
          src={cloudinaryThumb(product.image, 600)}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-sage-800 shadow-sm">
            {product.category}
          </span>
          {product.status === 'Rasprodat' && (
            <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider shadow-sm bg-red-500 text-white">
              {product.status}
            </span>
          )}
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex justify-between items-start mb-2 gap-3">
          <h3 className="text-xl font-serif font-bold text-earth-900 line-clamp-1">{product.name}</h3>
          <span className="flex items-center gap-1.5 text-sage-700 font-bold text-lg whitespace-nowrap">
            <Coins size={16} className="text-amber-500" />
            {product.price} <span className="text-xs font-bold text-sage-500">RSD</span>
            <span className="text-sm font-normal text-sage-400">/ {product.unit}</span>
          </span>
        </div>

        <p className="text-sage-800/70 text-sm mb-4 line-clamp-2 min-h-[2.5rem]">
          {product.description}
        </p>

        <div className="space-y-2.5 border-t border-sage-50 pt-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sage-700 min-w-0">
              <MapPin size={14} className="text-sage-600 flex-shrink-0" />
              <span className="text-xs font-medium truncate">{product.location}</span>
            </div>
            <div className="flex items-center gap-1 text-sage-500 text-[11px] flex-shrink-0">
              <Clock size={12} />
              <span>{product.createdAt}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sage-700 min-w-0">
            <User size={14} className="text-sage-600 flex-shrink-0" />
            <span className="text-xs truncate">{product.sellerName}</span>
          </div>
          {product.deliveryMethodLabel && (
            <div className="flex items-center gap-2 text-sage-700 min-w-0">
              <Truck size={14} className="text-sage-600 flex-shrink-0" />
              <span className="text-xs truncate">{product.deliveryMethodLabel}</span>
            </div>
          )}
          <div className="min-h-[30px] flex items-center">
            {product.sellerPhone && (
              isMobile ? (
                <a
                  href={`tel:${product.sellerPhone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-sage-50 hover:bg-sage-600 text-sage-800 hover:text-white rounded-full text-xs font-bold border border-sage-100 hover:border-sage-600 shadow-sm transition-all group/phone"
                >
                  <Phone size={13} className="group-hover/phone:rotate-12 transition-transform" />
                  <span>{product.sellerPhone}</span>
                </a>
              ) : (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-sage-50 text-sage-800 rounded-full text-xs font-bold border border-sage-100 shadow-sm">
                  <Phone size={13} />
                  <span>{product.sellerPhone}</span>
                </span>
              )
            )}
          </div>
        </div>

        <div className="mt-auto pt-6">
          {footerActions ?? (
            <button
              onClick={onClick}
              className="w-full py-3 bg-sage-50 text-sage-800 font-semibold rounded-xl hover:bg-sage-600 hover:text-white transition-all text-center"
            >
              Pogledaj detalje
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
